import { ReactNode, useCallback } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { StoredReader, TerminalReader } from '@frontend/payments-terminal-controller';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, styles as dsStyles } from '@frontend/design-system';
import { ReaderCard } from '../../select-terminal/select-terminal-step/reader-card';

const styles = {
  section: css`
    display: grid;
    grid-template-rows: 1fr auto;
    justify-items: center;
    padding: ${theme.spacing(2)} 0;
    gap: ${theme.spacing(2)};
  `,
  paymentForm: css`
    flex: 1;
    display: flex;
    gap: ${theme.spacing(2)};
    flex-direction: column;
    justify-content: center;
    width: 100%;
    max-width: ${theme.spacing(50)};
  `,
  readerSection: css`
    position: relative;
    height: 100%;
    display: flex;
    gap: ${theme.spacing(2)};
    flex-wrap: wrap;
    justify-content: center;
    overflow-y: auto;
    ${dsStyles.smallScrollbar(theme)}
  `,
  refreshButtonSection: css`
    display: flex;
    justify-content: center;
    padding-top: ${theme.spacing(1)};
  `,
};

type CollectReaderPaymentDisplayResponsiveProps = {
  selectedReader: StoredReader | undefined;
  ReaderStatusOverrideComponent?: ReactNode;
  availableReaders?: TerminalReader[];
  disableTerminalSelection?: boolean;
  refreshReadersBtnTrackingId?: string;
  isReadersLoading?: boolean;
  onSelectedReaderChange?: (reader: TerminalReader) => void;
  onRefetchReaders?: () => Promise<any>;
};

export const CollectReaderPaymentDisplayResponsive = ({
  selectedReader,
  isReadersLoading,
  ReaderStatusOverrideComponent,
  disableTerminalSelection = false,
  availableReaders,
  refreshReadersBtnTrackingId,
  onSelectedReaderChange,
  onRefetchReaders,
}: CollectReaderPaymentDisplayResponsiveProps) => {
  const { t } = useTranslation('payments');
  const readers = availableReaders || [];

  const onClickRefreshReaders = useCallback(async () => {
    if (typeof onRefetchReaders === 'function') {
      try {
        await onRefetchReaders();
      } catch (e) {
        // no-op
        console.error('CollectReaderPaymentDisplayResponsive > Error refreshing readers', e);
      }
    }
  }, [onRefetchReaders]);

  const onClickReaderCard = useCallback(
    (reader: TerminalReader) => {
      if (disableTerminalSelection) {
        return () => {};
      }

      return onSelectedReaderChange?.(reader);
    },
    [disableTerminalSelection, onSelectedReaderChange]
  );

  return (
    <>
      <section className='collect-reader-payment-display' css={styles.section}>
        <div css={styles.paymentForm}>{ReaderStatusOverrideComponent}</div>

        {readers?.length && (
          <div css={styles.readerSection}>
            {isReadersLoading && <ContentLoader show={isReadersLoading} message={t('Discovering terminals...')} />}
            {readers.map((reader) => (
              <ReaderCard
                key={reader.id}
                reader={reader}
                isConnected={false}
                disabled={disableTerminalSelection}
                selected={selectedReader?.readerId === reader.id}
                onClick={onClickReaderCard}
              />
            ))}
          </div>
        )}

        {typeof onRefetchReaders === 'function' && (
          <div css={styles.refreshButtonSection}>
            <Button
              variant='secondary'
              onClick={onClickRefreshReaders}
              trackingId={refreshReadersBtnTrackingId}
              disabled={isReadersLoading}
              iconName='update'
            >
              {isReadersLoading ? t('Refreshing Terminals') : t('Refresh Terminals')}
            </Button>
          </div>
        )}
      </section>
    </>
  );
};
